// Tenant-shaped layout: fetches the tenant once, renders the
// PageHeader (title + subtitle with VaultSyncPill) + tab strip, and
// gives child routes an <Outlet /> below. Mounted on the layout
// route at routes/accounts.$accountSlug.tenants.$tenantSlug.tsx so
// the tabs stay visible on /secrets and /ha-instances too.

import { useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";

import { tenants } from "../../api/endpoints/tenants";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { PageHeader } from "../../components/shell/PageHeader";

import { VaultSyncPill } from "./VaultSyncPill";

interface TenantLayoutProps {
  accountSlug: string;
  tenantSlug: string;
}

export function TenantLayout({ accountSlug, tenantSlug }: TenantLayoutProps) {
  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.tenant(accountSlug, tenantSlug),
    queryFn: () => tenants.get(accountSlug, tenantSlug).then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title={q.data?.display_name ?? tenantSlug}
        subtitle={
          q.data ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <code>{q.data.slug}</code>
              <VaultSyncPill
                status={q.data.vault_sync_status}
                title={q.data.vault_sync_last_error ?? undefined}
              />
            </span>
          ) : (
            tenantSlug
          )
        }
      />

      {q.error && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBanner error={q.error} title="Failed to load tenant" />
        </div>
      )}

      <nav style={tabStrip} aria-label="Tenant sub-resources">
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug"
          params={{ accountSlug, tenantSlug }}
          activeOptions={{ exact: true }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Overview
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites"
          params={{ accountSlug, tenantSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Sites
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/ha-instances"
          params={{ accountSlug, tenantSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          HA Instances
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/identity"
          params={{ accountSlug, tenantSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Identity
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/secrets"
          params={{ accountSlug, tenantSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Secrets
        </Link>
      </nav>

      <Outlet />
    </>
  );
}

const tabStrip: React.CSSProperties = {
  display: "flex",
  gap: 4,
  marginBottom: 16,
  borderBottom: "1px solid var(--border)",
};

const tabStyle: React.CSSProperties = {
  padding: "6px 12px",
  fontSize: "0.85rem",
  color: "var(--text)",
  textDecoration: "none",
  borderBottom: "2px solid transparent",
};

const activeTabStyle: React.CSSProperties = {
  ...tabStyle,
  color: "var(--accent)",
  borderBottomColor: "var(--accent)",
  fontWeight: 600,
};
